/**
 * Actividad de la persona en el backoffice (teclado, ratón, toque, desplazamiento), compartida entre pestañas. El
 * cliente del API la manda en cada petición (`IDLE_HEADER`): así las consultas automáticas de una ventana que nadie
 * usa no mantienen la sesión abierta, y el API la cierra al pasar el límite de inactividad.
 */
const KEY = 'pqrs.activity';
const ENDED_KEY = 'pqrs.session.ended';
const STARTED_KEY = 'pqrs.session.started';
const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;
let last = Date.now();
let written = 0;

function read(): number {
  try { return Number(localStorage.getItem(KEY)) || 0; } catch { return 0; }
}

/** La persona usó la pantalla. Se escribe a lo sumo cada 5 s para no castigar el almacenamiento. */
export function markActivity() {
  last = Date.now();
  if (last - written < 5_000) return;
  written = last;
  try { localStorage.setItem(KEY, String(last)); } catch { /* sin almacenamiento: vale la actividad de esta pestaña */ }
}

/** Segundos sin actividad en ninguna pestaña. */
export function idleSeconds(): number {
  return Math.max(0, Math.floor((Date.now() - Math.max(last, read())) / 1000));
}

export function startActivityTracking(): () => void {
  markActivity();
  const on = () => markActivity();
  for (const e of EVENTS) window.addEventListener(e, on, { passive: true, capture: true });
  return () => { for (const e of EVENTS) window.removeEventListener(e, on, { capture: true }); };
}

/**
 * `SESSION_REPLACED` y `SESSION_CONFLICT_TIMEOUT` los dice el API (sesiones simultáneas); `SESSION_SUITE_LOGOUT`, que la
 * persona cerró sesión en la suite o en otra de sus apps (back-channel logout); `SESSION_LEFT` es que la persona eligió
 * desde aquí continuar en la otra sesión. «Salir» no es un motivo: esa pestaña va al cierre del SSO y las demás se
 * enteran por el back-channel (su siguiente petición responde 401 `SESSION_SUITE_LOGOUT`).
 */
export type SessionEndReason = 'SESSION_IDLE' | 'SESSION_EXPIRED' | 'SESSION_REVOKED' | 'SESSION_REPLACED' | 'SESSION_CONFLICT_TIMEOUT' | 'SESSION_SUITE_LOGOUT' | 'SESSION_LEFT';

/**
 * Avisa a las demás pestañas que la sesión terminó, con el motivo. Solo la pestaña que se ve va a ingresar; las demás
 * esperan a que haya sesión nueva (`LoginPage`).
 */
export function announceSessionEnd(reason: SessionEndReason) {
  try {
    localStorage.setItem(ENDED_KEY, `${reason}:${Date.now()}`);
  } catch { /* nada que avisar */ }
}

/** Escucha el fin de la sesión en otra pestaña. */
export function onSessionEndElsewhere(fn: (reason: SessionEndReason) => void): () => void {
  const on = (e: StorageEvent) => {
    if (e.key !== ENDED_KEY || !e.newValue) return;
    fn(e.newValue.split(':')[0] as SessionEndReason);
  };
  window.addEventListener('storage', on);
  return () => window.removeEventListener('storage', on);
}

/**
 * Hay sesión en esta pestaña: la vigencia (`expiresAt`) la distingue de la anterior, así que solo un ingreso nuevo
 * cambia el valor y avisa a las pestañas que esperaban (`onSessionStarted`).
 */
export function markSessionStarted(id: string) {
  try {
    if (localStorage.getItem(STARTED_KEY) !== id) localStorage.setItem(STARTED_KEY, id);
  } catch { /* sin almacenamiento: las demás pestañas se enteran al volver a verse */ }
}

/** Otra pestaña abrió una sesión nueva en este navegador. */
export function onSessionStarted(fn: () => void): () => void {
  const on = (e: StorageEvent) => { if (e.key === STARTED_KEY && e.newValue) fn(); };
  window.addEventListener('storage', on);
  return () => window.removeEventListener('storage', on);
}
